import React, { useState } from 'react';
import { format } from 'date-fns';
import MobileBookingCard from './MobileBookingCard';
import { Calendar as CalendarIcon, Settings as SettingsIcon } from 'lucide-react';

/**
 * Mobile-optimized Bookings Management View
 * Features:
 * - 1 card per row layout
 * - Sticky filters bar at top
 * - Month-grouped sections with proper scrolling
 * - Compact design optimized for phone screens
 */
function MobileBookingsView({ 
  bookings,
  onEditBooking,
  getStatusBadge,
  getPaymentStatusBadge,
  loading
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showFilters, setShowFilters] = useState(false);
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  // Filter bookings
  const filteredBookings = bookings.filter(booking => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const guestName = (booking.user?.name || booking.guestName || '').toLowerCase();
      const guestEmail = (booking.user?.email || booking.guestEmail || '').toLowerCase();
      const roomNumber = (booking.room?.roomNumber || booking.bed?.roomNumber || '').toString().toLowerCase();
      
      if (!guestName.includes(searchLower) && 
          !guestEmail.includes(searchLower) && 
          !roomNumber.includes(searchLower)) {
        return false;
      }
    }
    
    // Status filter
    if (statusFilter !== 'ALL' && booking.status !== statusFilter) {
      return false;
    }
    
    // Date range filter
    if (startDateFilter && booking.checkIn) {
      const checkInDate = new Date(booking.checkIn).setHours(0, 0, 0, 0);
      const startDate = new Date(startDateFilter).setHours(0, 0, 0, 0);
      if (checkInDate < startDate) return false;
    }
    
    if (endDateFilter && booking.checkIn) {
      const checkInDate = new Date(booking.checkIn).setHours(0, 0, 0, 0);
      const endDate = new Date(endDateFilter).setHours(0, 0, 0, 0);
      if (checkInDate > endDate) return false;
    }
    
    return true;
  });

  // Group by month
  const groupByMonth = (bookings) => {
    const groups = {};
    bookings.forEach(booking => {
      if (booking.checkIn) {
        const monthKey = format(new Date(booking.checkIn), 'yyyy-MM');
        const monthLabel = format(new Date(booking.checkIn), 'MMMM yyyy');
        if (!groups[monthKey]) {
          groups[monthKey] = {
            label: monthLabel,
            bookings: []
          };
        }
        groups[monthKey].bookings.push(booking);
      } else {
        if (!groups['no-date']) {
          groups['no-date'] = {
            label: 'No Check-in Date',
            bookings: []
          };
        }
        groups['no-date'].bookings.push(booking);
      }
    });
    return groups;
  };

  const monthGroups = groupByMonth(filteredBookings);
  const sortedMonthKeys = Object.keys(monthGroups).sort((a, b) => {
    if (a === 'no-date') return 1;
    if (b === 'no-date') return -1;
    return b.localeCompare(a); // Descending order (newest first)
  });

  // Stats
  const stats = {
    total: filteredBookings.length,
    confirmed: filteredBookings.filter(b => b.status === 'CONFIRMED').length,
    checkedIn: filteredBookings.filter(b => b.status === 'CHECKEDIN').length,
    pending: filteredBookings.filter(b => b.status === 'PENDING').length
  };

  return (
    <div style={{ 
      backgroundColor: '#F5F3EB',
      minHeight: '100vh',
      paddingBottom: '80px', // Space for bottom navigation
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      boxSizing: 'border-box'
    }}>
      {/* Sticky Header with Stats */}
      <div style={{
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        borderBottom: '1px solid #E3DFD3',
        zIndex: 50,
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        {/* Title and Toggle */}
        <div style={{ 
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ 
              margin: '0 0 4px 0', 
              fontSize: '18px', 
              fontWeight: '700', 
              color: '#1A3E2A' 
            }}>
              Bookings
            </h2>
            <p style={{ margin: '0', fontSize: '12px', color: '#5F6B61' }}>
              {stats.total} {stats.total === 1 ? 'booking' : 'bookings'}
            </p>
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              backgroundColor: showFilters ? '#1A3E2A' : '#EEEBE1',
              color: showFilters ? 'white' : '#5F6B61',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            Filters {showFilters ? '▲' : '▼'}
          </button>
        </div>

        {/* Quick Stats */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: '8px',
          padding: '0 16px 12px 16px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{ 
            minWidth: 0,
            textAlign: 'center',
            backgroundColor: '#E1E9E2',
            padding: '8px 4px',
            borderRadius: '8px',
            border: '1px solid #1A3E2A'
          }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#0F2619' }}>
              {stats.confirmed}
            </div>
            <div style={{ fontSize: '10px', color: '#0F2619' }}>Confirmed</div>
          </div>
          <div style={{ 
            minWidth: 0,
            textAlign: 'center',
            backgroundColor: '#E1E9E2',
            padding: '8px 4px',
            borderRadius: '8px',
            border: '1px solid #1A3E2A'
          }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#0F2619' }}>
              {stats.checkedIn}
            </div>
            <div style={{ fontSize: '10px', color: '#0F2619' }}>Checked In</div>
          </div>
          <div style={{ 
            minWidth: 0,
            textAlign: 'center',
            backgroundColor: '#F3E6CB',
            padding: '8px 4px',
            borderRadius: '8px',
            border: '1px solid #B68235'
          }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#684A1F' }}>
              {stats.pending}
            </div>
            <div style={{ fontSize: '10px', color: '#684A1F' }}>Pending</div>
          </div>
        </div>

        {/* Filters Dropdown */}
        {showFilters && (
          <div style={{ 
            padding: '12px 16px',
            backgroundColor: '#F5F3EB',
            borderTop: '1px solid #E3DFD3'
          }}>
            {/* Search */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '4px', 
                fontSize: '12px', 
                fontWeight: '600', 
                color: '#36433B' 
              }}>
                Search
              </label>
              <input
                type="text"
                placeholder="Guest name, email, or room..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Status Filter */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '4px', 
                fontSize: '12px', 
                fontWeight: '600', 
                color: '#36433B' 
              }}>
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  boxSizing: 'border-box'
                }}
              >
                <option value="ALL">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="CONFIRMED">Confirmed</option>
                <option value="CHECKEDIN">Checked In</option>
                <option value="CHECKEDOUT">Checked Out</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>

            {/* Date Range */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '4px', 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: '#36433B' 
                }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDateFilter}
                  onChange={(e) => setStartDateFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '8px',
                    fontSize: '12px',
                    backgroundColor: 'white',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '4px', 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: '#36433B' 
                }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={endDateFilter}
                  onChange={(e) => setEndDateFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '8px',
                    fontSize: '12px',
                    backgroundColor: 'white',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Clear Filters */}
            {(searchTerm || statusFilter !== 'ALL' || startDateFilter || endDateFilter) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('ALL');
                  setStartDateFilter('');
                  setEndDateFilter('');
                }}
                style={{
                  width: '100%',
                  marginTop: '12px',
                  padding: '8px',
                  backgroundColor: '#A13F2F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ✕ Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Bookings Content */}
      <div style={{ padding: '12px 16px', width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
        {loading ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: '#5F6B61'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}><SettingsIcon size="1em" aria-hidden="true" /></div>
            <div>Loading bookings...</div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '2px dashed #D2CDBF'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}><CalendarIcon size="1em" aria-hidden="true" /></div>
            <h3 style={{ margin: '0 0 8px 0', color: '#36433B', fontSize: '16px' }}>
              No bookings found
            </h3>
            <p style={{ color: '#5F6B61', margin: '0', fontSize: '14px' }}>
              {bookings.length === 0 
                ? 'Bookings will appear here' 
                : 'Try adjusting your filters'}
            </p>
          </div>
        ) : (
          sortedMonthKeys.map(monthKey => (
            <div key={monthKey} style={{ marginBottom: '24px' }}>
              {/* Month Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
                padding: '8px 12px',
                backgroundColor: '#F5F3EB',
                borderRadius: '8px',
                border: '1px solid #E3DFD3'
              }}>
                <h3 style={{ 
                  margin: '0', 
                  fontSize: '14px', 
                  fontWeight: '700',
                  color: '#24332A',
                  flex: 1
                }}>
                  {monthGroups[monthKey].label}
                </h3>
                <span style={{
                  backgroundColor: '#1A3E2A',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  {monthGroups[monthKey].bookings.length}
                </span>
              </div>

              {/* Booking Cards - 1 column grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr)',
                gap: '12px',
                width: '100%',
                maxWidth: '100%'
              }}>
                {monthGroups[monthKey].bookings.map(booking => (
                  <MobileBookingCard
                    key={booking.id}
                    booking={booking}
                    onEdit={onEditBooking}
                    getStatusBadge={getStatusBadge}
                    getPaymentStatusBadge={getPaymentStatusBadge}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default MobileBookingsView;
